import React, { useState } from "react";
import { X, Boxes, DollarSign, FileText, Loader2 } from "lucide-react";
import { IThirdPartyRequest } from "../../../models/thirdPartyRequest.model";
import {
  ICreateThirdPartyInwardPayload,
  IInwardItemPayload,
} from "../../../api/Factory/store.api";
import { ProductMaterialSelect } from "../../../components/Factory/ProductMaterialSelect";
import toast from "react-hot-toast";

interface InwardToStoreModalProps {
  isOpen: boolean;
  request: IThirdPartyRequest | null;
  onSubmit: (payload: ICreateThirdPartyInwardPayload) => Promise<void>;
  onClose: () => void;
  isLoading?: boolean;
}

export default function ThirdPartyInwardToStoreModal({
  isOpen,
  request,
  onSubmit,
  onClose,
  isLoading = false,
}: InwardToStoreModalProps) {
  const [productionCost, setProductionCost] = useState<number>(0);
  const [orderDate, setOrderDate] = useState<string>("");
  const [recieveDate, setRecieveDate] = useState<string>("");
  const [invoiceNumber, setInvoiceNumber] = useState<string>("");
  const [receivingTransportCost, setReceivingTransportCost] =
    useState<number>(0);
  const [receivingExtraCost, setReceivingExtraCost] = useState<number>(0);
  const [remark, setRemark] = useState<string>("");
  const [selectedQty, setSelectedQty] = useState<number>(0);
  const [productionCostType, setProductionCostType] = useState<
    "unit" | "total"
  >("unit");
  const [inwardItems, setInwardItems] = useState<IInwardItemPayload[]>([
    { itemId: "", itemType: "material", quantity: 1, unit: "piece" },
  ]);

  if (!isOpen || !request) return null;
  //   const handleAddItem = () => {
  //     setInwardItems((prev) => [
  //       ...prev,
  //       { itemId: "", itemType: "product", quantity: 1, unit: "pcs" },
  //     ]);
  //   };

  const handleRemoveItem = (index: number) => {
    if (inwardItems.length === 1) return;
    setInwardItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleTypeChange = (index: number, newType: "material" | "product") => {
    setInwardItems((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        itemType: newType,
        itemId: "",
        unit: newType === "product" ? "pcs" : "",
      };
      return updated;
    });
  };

  const handleSelectItem = (
    index: number,
    selected: { value: string; label: string; unit?: string },
  ) => {
    setInwardItems((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        itemId: selected.value,
        unit: selected.unit || updated[index].unit || "pcs",
      };
      return updated;
    });
  };

  const handleItemChange = (
    index: number,
    field: keyof IInwardItemPayload,
    value: any,
  ) => {
    if (field === "quantity") {
      setSelectedQty(value);
    }
    setInwardItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const invalidItem = inwardItems.find(
      (item) => !item.itemId.trim() || item.quantity <= 0,
    );
    if (invalidItem) {
      toast.error(
        "Please ensure all inward items have a selected Item and valid Quantity.",
      );
      return;
    }
    if (invoiceNumber.trim() === "") {
      toast.error("Enter invoice number");
      return;
    }
    if (orderDate.trim() === "") {
      toast.error("Enter Order Date");
      return;
    }
    if (recieveDate.trim() === "") {
      toast.error("Enter Recieved Date");
      return;
    }

    await onSubmit({
      thirdPartyRequestId: request._id,
      productionCost:
        productionCostType === "unit"
          ? selectedQty * Number(productionCost)
          : Number(productionCost),
      receivingTransportCost: Number(receivingTransportCost),
      receivingExtraCost: Number(receivingExtraCost),
      inwardItems,
      remark,
      invoiceNo: invoiceNumber,
      orderDate: orderDate,
      recieveDate: recieveDate,
    });
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 overflow-y-auto">
      <div className="relative w-full max-w-3xl rounded-2xl bg-white shadow-xl border border-slate-100 my-8">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h3 className="text-lg font-bold text-slate-800">
              Inward to Store
            </h3>
            <p className="text-xs text-slate-500">
              Request No:{" "}
              <span className="font-semibold text-slate-700">
                {request.requestNo}
              </span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Cost Allocation Inputs */}
          <div className="space-y-3">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
              Receiving & Production Costs
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-1.5">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Cost type
                </label>
                <select
                  value={productionCostType}
                  onChange={(e) =>
                    setProductionCostType(e.target.value as "unit" | "total")
                  }
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                >
                  <option value="unit">Unit</option>
                  <option value="total">TotalCost</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Production Cost
                </label>
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={productionCost}
                  onChange={(e) => setProductionCost(Number(e.target.value))}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Receiving Transport
                </label>
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={receivingTransportCost}
                  onChange={(e) =>
                    setReceivingTransportCost(Number(e.target.value))
                  }
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Receiving Extra
                </label>
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={receivingExtraCost}
                  onChange={(e) =>
                    setReceivingExtraCost(Number(e.target.value))
                  }
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
              Invoice Details
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Invoice No.
                </label>
                <input
                  type="text"
                  step="any"
                  value={invoiceNumber}
                  placeholder="Invoice No"
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Order Date
                </label>
                <input
                  type="date"
                  value={orderDate}
                  onChange={(e) => setOrderDate(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Recieve Date
                </label>
                <input
                  type="date"
                  value={recieveDate}
                  onChange={(e) => setRecieveDate(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
              </div>
            </div>
          </div>
          {/* Dynamic Inward Items Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                <Boxes size={14} /> Inward Item
              </h4>
              {/* <button
                type="button"
                onClick={handleAddItem}
                className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-700"
              >
                <Plus size={14} /> Add Item
              </button> */}
            </div>

            <div className="space-y-3 max-h-56 overflow-y-auto p-1">
              {inwardItems.map((item, index) => (
                <ProductMaterialSelect
                  key={index}
                  item={item}
                  index={index}
                  handleTypeChange={handleTypeChange}
                  handleSelectItem={handleSelectItem}
                  handleItemChange={handleItemChange}
                  handleRemoveItem={handleRemoveItem}
                  canRemove={inwardItems.length > 1}
                />
              ))}
            </div>
          </div>

          {/* Remark */}
          <div>
            <label className="flex text-xs font-medium text-slate-600 mb-1 items-center gap-1">
              <FileText size={14} /> Remarks
            </label>
            <textarea
              rows={2}
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder="Optional notes regarding this inward entry..."
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            />
          </div>

          {/* Modal Actions */}
          <div className="flex items-center justify-end gap-3 border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-2 text-xs font-medium text-white hover:bg-emerald-700 transition disabled:opacity-50"
            >
              {isLoading && <Loader2 size={14} className="animate-spin" />}
              Submit Inward
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
